import React, { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { SparklesIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid'; // Added error icon
import { useLocation } from 'react-router-dom';

// Updated import paths with an additional '../'
import UrlConfig from '../../data/UrlConfig';
import Common from '../../common/Common';

const API_URLS = UrlConfig;
const HOLOTROPIC_VIDEO_URL = "https://gusaguiar.com/assets/gamma-activation-kit/Bashar - Gamma Holotropic Meditation.mp4";

// Renamed component
const BasharGammaTransmissionLeadMagnet = () => {
  const [name, setName] = useState(localStorage.getItem('track_user_name') || '');
  const [email, setEmail] = useState(localStorage.getItem('track_user_email') || '');
  const [wantsGeneralBasharUpdates, setWantsGeneralBasharUpdates] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [videoErrors, setVideoErrors] = useState({}); // State to track video loading errors

  const location = useLocation();

  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  // Function to handle video errors
  const handleVideoError = (videoUrl) => {
    console.error(`Failed to load video: ${videoUrl}`);
    setVideoErrors(prevErrors => ({
        ...prevErrors,
        [videoUrl]: true
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');
    setError('');

    name.trim() && localStorage.setItem('track_user_name', name.trim());
    email.trim() && localStorage.setItem('track_user_email', email.trim());
    
    // Basic frontend validation
    if (!name.trim() || !email.trim()) {
      setError('Please enter your name and email.');
      setIsLoading(false);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      setIsLoading(false);
      return;
    }

    // Always subscribe to the gamma transmission lead list
    const newsletterSubscriptions = ['going-gamma-full'];
    // Add general Bashar updates if the user opted in
    if (wantsGeneralBasharUpdates) {
      newsletterSubscriptions.push('and-more-bashar-updates'); // A new, more generic list for Bashar updates
    }

    try {
      const response = await fetch(API_URLS.leads_add, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          nick: name,
          folder: 'going-gamma-full',
          page: location.pathname,
          lang: 'en',
          newsletter: newsletterSubscriptions,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Thank you! Your exclusive Bashar Gamma Transmission and gifts are on their way to your inbox.');
        setName('');
        setEmail('');
        setWantsGeneralBasharUpdates(false); // Reset checkbox
        setSubmitted(true);
      } else {
        throw new Error(data.message || 'An error occurred while processing your request.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-black text-white min-h-screen font-sans flex flex-col">
      {/* Gradient background similar to HomePage */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-black to-gray-900 opacity-95"></div>

      <main
        ref={ref}
        className={`relative z-10 flex-grow container mx-auto px-4 py-12 flex flex-col items-center
          ${inView ? "fade-in-animation" : "opacity-0"}
        `}
      >
        {/* Section for the Holotropic Meditation Video - Presented upfront */}
        <h1 className="text-3xl sm:text-4xl font-bold text-center mb-4">
          Experience Deep Relaxation: Your Free Holotropic Breathwork Guided Meditation
        </h1>
        <p className="text-base sm:text-lg text-gray-400 text-center max-w-2xl mb-6">
          Start your journey to expanded consciousness right now. Play the guided meditation below:
        </p>

        <div className="w-full max-w-2xl mb-12">
          <div className="aspect-video w-full rounded-xl overflow-hidden shadow-lg shadow-sky-500/30 bg-black">
            {/* Conditional Rendering: Show video or error message */}
            {videoErrors[HOLOTROPIC_VIDEO_URL] ? (
              <div className="w-full h-full bg-red-950/20 flex flex-col items-center justify-center text-center p-4">
                  <ExclamationTriangleIcon className="w-10 h-10 text-red-400 mb-3" />
                  <p className="font-semibold text-red-300 mb-2">The video player could not load.</p>
                  <a
                      href={HOLOTROPIC_VIDEO_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block py-2 px-5 rounded-lg bg-red-500 text-white font-bold hover:bg-red-400 transition-all"
                  >
                      Open Video in New Tab
                  </a>
              </div>
            ) : (
              <video
                width="100%"
                height="auto"
                controls
                poster="/images/holotropic-meditation-poster.jpg"
                className="w-full h-full object-cover"
                onError={() => handleVideoError(HOLOTROPIC_VIDEO_URL)} // Attach error handler
              >
                <source src={HOLOTROPIC_VIDEO_URL} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}
          </div>
        </div>

        {/* Section for the new Lead Magnet (Bashar Gamma Transmission) */}
        <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4 text-sky-400">
          Ready for More? Unlock Exclusive Bashar Gamma Transmissions!
        </h2>
        <p className="text-base sm:text-lg text-gray-400 text-center max-w-2xl mb-10">
          Dive deeper into consciousness. Submit your details below to receive instant access to the **full 4-hour Bashar Gamma Transmission** and other exclusive gifts directly to your inbox.
        </p>

        <div className="w-full max-w-md bg-black/30 border border-gray-700 shadow-lg shadow-gray-700/30 rounded-xl p-6 sm:p-8">
          {submitted ? (
            <div className="text-center text-green-400 p-6 bg-green-900/30 border border-green-700 rounded-lg">
              <CheckCircleIcon className="w-12 h-12 mx-auto mb-3 text-green-400" />
              <p className="text-xl font-semibold">{message}</p>
              <p className="text-lg mt-2">Check your email for access details.</p>
              {wantsGeneralBasharUpdates && (
                <p className="text-sm text-gray-300 mt-4">
                  You'll also receive general updates and insights from Bashar.
                </p>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col space-y-6">
              <div>
                <label htmlFor="name" className="block text-gray-300 text-sm font-medium mb-2">
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full p-3 rounded-md bg-gray-800 border border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-sky-400"
                  placeholder="John Doe"
                  required
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-gray-300 text-sm font-medium mb-2">
                  Your Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-3 rounded-md bg-gray-800 border border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-sky-400"
                  placeholder="your.email@example.com"
                  required
                  disabled={isLoading}
                />
              </div>

              {/* Checkbox for general Bashar updates (optional) */}
              <div className="flex items-center">
                <input
                  id="generalBasharUpdates"
                  type="checkbox"
                  checked={wantsGeneralBasharUpdates}
                  onChange={(e) => setWantsGeneralBasharUpdates(e.target.checked)}
                  className="h-4 w-4 text-sky-400 focus:ring-sky-400 border-gray-600 rounded bg-gray-800 cursor-pointer"
                  disabled={isLoading}
                />
                <label htmlFor="generalBasharUpdates" className="ml-3 block text-sm text-gray-300 cursor-pointer">
                  Yes, also send me general updates and insights from Bashar.
                </label>
              </div>

              {(error || message) && (
                  <p className={`text-sm text-center ${error ? 'text-red-400' : 'text-green-400'}`}>
                      {error || message}
                  </p>
              )}

              <button
                type="submit"
                className="w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-gradient-to-r from-sky-500 to-purple-600 text-white font-semibold text-lg
                           hover:from-sky-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-400
                           transition-all duration-300 ease-in-out shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? (
                    <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Getting your access...
                    </>
                ) : (
                    <>
                        Get My Exclusive Access <SparklesIcon className="w-5 h-5" />
                    </>
                )}
              </button>
            </form>
          )}
        </div>
      </main>

      <footer className="relative z-10 text-center py-6 text-xs text-gray-600">
        © {new Date().getFullYear()} Gus • Aguiar | On a mission to uplift and transform.
      </footer>
    </div>
  );
};

export default BasharGammaTransmissionLeadMagnet;